import React, { useEffect, useState } from "react";
import { FaLocationDot } from "react-icons/fa6";
import { FaHome, FaPhoneAlt, FaSearch, FaUserSlash } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { SearchUsersAsync, selectquery } from "./searchSlice";
import { Link, useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";

function Search() {
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const query = useSelector(selectquery);
  const navigation = useNavigate();
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (inputValue.toString().length > 5) {
        setIsLoading(true);
        dispatch(SearchUsersAsync(inputValue)).finally(() => setIsLoading(false));
      } else {
        dispatch(SearchUsersAsync(null));
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [dispatch, inputValue]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (inputValue.length > 5) {
      setIsLoading(true);
      dispatch(SearchUsersAsync(inputValue)).finally(() => setIsLoading(false));
    }
  };

  const handleProfile = (id) => {
    setInputValue("");
    dispatch(SearchUsersAsync(null));
    navigation(`/profile/${id}`);
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="relative z-10">
        {/* Header Section */}
        <div className="pt-12 pb-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
              Find Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-400">Community</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Connect with people in your area by searching with phone numbers. Build meaningful relationships within your community.
            </p>
          </div>
        </div>

        {/* Search Section */}
        <div className="px-4 sm:px-6 lg:px-8 mb-12">
          <div className="max-w-2xl mx-auto">
            <form onSubmit={onSubmit} className="relative">
              <div className="relative">
                <input
                  type="search"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  className="w-full h-14 pl-6 pr-16 text-lg text-gray-900 bg-white border border-gray-300 rounded-2xl shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300 placeholder-gray-500"
                  placeholder="Enter phone number to search..."
                  required
                  aria-label="Search by phone number"
                />
                <button
                  type="submit"
                  disabled={inputValue.length <= 5 || isLoading}
                  className="absolute right-2 top-2 h-10 w-10 bg-blue-600 text-white rounded-xl hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-500/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  aria-label="Search"
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <FaSearch className="w-4 h-4" />
                  )}
                </button>
              </div>
              {inputValue.length > 0 && inputValue.length <= 5 && (
                <p className="mt-2 text-sm text-yellow-600 pl-2">
                  Please enter more than 5 characters to search
                </p>
              )}
            </form>
          </div>
        </div>

        {/* Results Section */}
        <div className="px-4 sm:px-6 lg:px-8 pb-20">
          <div className="max-w-4xl mx-auto">
            {query && query.query && query.query.length > 0 ? (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                  Search Results ({query.query.length} found)
                </h2>
                {query.query.map((profile, index) => (
                  <div
                    key={profile._id || index}
                    className="group bg-gray-50 border border-gray-200 rounded-2xl overflow-hidden hover:bg-gray-100 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl"
                  >
                    <div className="flex flex-col md:flex-row items-center p-6 gap-6">
                      {/* Profile Image */}
                      <div className="relative w-24 h-24 md:w-32 md:h-32 flex-shrink-0">
                        <img
                          className="w-full h-full object-cover rounded-2xl border-2 border-gray-200 shadow-lg"
                          loading="lazy"
                          src={profile.imageURL || "https://via.placeholder.com/150"}
                          alt={`Profile of ${profile.नाम || 'User'}`}
                        />
                        <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-black/10 to-transparent"></div>
                      </div>

                      {/* Profile Info */}
                      <div className="flex-grow text-center md:text-left space-y-3">
                        <h3 className="text-2xl font-bold text-gray-900">
                          {profile.नाम || "Name Not Available"}
                        </h3>
                        
                        <div className="flex flex-col sm:flex-row gap-4">
                          <div className="flex items-center justify-center md:justify-start gap-2 text-gray-600">
                            <FaLocationDot className="text-blue-600 flex-shrink-0" />
                            <span className="text-sm font-medium">
                              {profile.जिला || "Location Not Available"}
                            </span>
                          </div>
                          
                          <div className="flex items-center justify-center md:justify-start gap-2 text-gray-600">
                            <FaPhoneAlt className="text-green-600 flex-shrink-0" />
                            <span className="text-sm font-medium">
                              {profile.मो || "Phone Not Available"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Action Button */}
                      <div className="flex-shrink-0">
                        <button
                          onClick={() => handleProfile(profile._id)}
                          className="group/btn bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-300 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-blue-500/30 shadow-lg"
                          aria-label={`View profile of ${profile.नाम || 'user'}`}
                        >
                          <span className="flex items-center gap-2">
                            View Profile
                            <svg
                              className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform duration-300"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : inputValue.length > 5 ? (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-24 h-24 bg-gray-100 rounded-full mb-6">
                  <FaUserSlash className="text-gray-400" size={40} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">No Users Found</h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  We couldn't find any users with that phone number. Please check the number and try again.
                </p>
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-24 h-24 bg-gray-100 rounded-full mb-6">
                  <FaSearch className="text-gray-400" size={40} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Start Your Search</h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  Enter a phone number above to find and connect with people in your community.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Search;
// export default Search;
